import React from "react";
import { Tabs } from "@shared/ui/tabs";
import { ClipboardList, Clock, CheckCircle, AlertCircle, Plus } from "lucide-react";
import { Button } from "@shared/ui/button";
import { MOCK_REQUESTS } from "./mockData";
import AllRequests from "./tabs/AllRequests";
import Complaints from "./tabs/Complaints";
import ServiceRequests from "./tabs/ServiceRequests";
import OtherRequests from "./tabs/OtherRequests";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";
import { useConsumerRequests } from "../../../../requests/hook";
import { useAllAccounts } from "../../../../shared/hook";
import { getLoginDataFromStorage } from "@shared/utils/loginUtils";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useTranslation } from "react-i18next";

export default function AllRequestsMainTab() {
  const { t } = useTranslation();
  const { consumerId } = getLoginDataFromStorage();
  const remoteUtilityId = getRemoteUtilityId();

  const { data: allAccounts } = useAllAccounts({
    page: 1,
    limit: 1000,
    active_consumers: 1,
    remote_utility_id: remoteUtilityId,
    is_sub_account: 1,
    consumer_id: consumerId,
  });

  const allConsumerIds = React.useMemo(() => {
    const subIds = allAccounts?.results?.map((acc: any) => acc.id) || [];
    return subIds;
  }, [allAccounts, consumerId]);

  const { data: requestData, isLoading } = useConsumerRequests({
      page: 1,
      limit: 10,
      consumer_id: allConsumerIds,
      remote_utility_id: remoteUtilityId,
      request_type: undefined,
    });
  return (
    <div className="space-y-6 mt-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <QuickStatsCard
          title={t("Total Requests")}
          displayValue={<span className="text-[22px] font-bold text-gray-900">0</span>}
          subtext={t("All types & accounts")}
          trend={0}
          trendSummary=""
          icon={
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600">
              <ClipboardList className="w-5 h-5" />
            </div>
          }
          className="shadow-sm border-gray-100 rounded-xl"
        />
        <QuickStatsCard
          title={t("Active / Open")}
          displayValue={<span className="text-[22px] font-bold text-orange-500">0</span>}
          subtext={t("In progress or pending")}
          trend={0}
          trendSummary=""
          icon={
            <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center text-orange-500">
              <Clock className="w-5 h-5" />
            </div>
          }
          className="shadow-sm border-gray-100 rounded-xl"
        />
        <QuickStatsCard
          title={t("Resolved / Closed")}
          displayValue={<span className="text-[22px] font-bold text-green-500">0</span>}
          subtext={t("Completed requests")}
          trend={0}
          trendSummary=""
          icon={
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center text-green-500">
              <CheckCircle className="w-5 h-5" />
            </div>
          }
          className="shadow-sm border-gray-100 rounded-xl"
        />
        <QuickStatsCard
          title={t("Accounts with Open Items")}
          displayValue={<span className="text-[22px] font-bold text-red-500">0</span>}
          subtext={t("of {{count}} total accounts", { count: allAccounts?.count || 0 })}
          trend={0}
          trendSummary=""
          icon={
            <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center text-red-500">
              <AlertCircle className="w-5 h-5" />
            </div>
          }
          className="shadow-sm border-gray-100 rounded-xl"
        />
      </div>

      {/* Sub Tabs */}
      <Tabs
        defaultValue="all"
        disableUrlSync={true}
        tabComponents={{
          "all": {
            label: t("All"),
            count: requestData?.count,
            component: <AllRequests />
          },
          "complaints": {
            label: t("Complaints"),
            count: requestData?.counts?.complaint,
            component: <Complaints />
          },
          "service": {
            label: t("Service Requests"),
            count: requestData?.counts?.service,
            component: <ServiceRequests />
          },
          "other": {
            label: t("Other Requests"),
            count: requestData?.counts?.other,
            component: <OtherRequests />
          }
        }}
      />
    </div>
  );
}
